import { useQuery } from "@apollo/client/react";
import { GET_USERS_LIST } from "@/hooks";
import { enqueueSnackbar } from "notistack";
import { useEffect } from "react";

/**
 * Custom hook to fetch and format the users list.
 * @param {string} [searchString] - The search string to filter users.
 * @param {string} [statusFilter] - The status filter to apply.
 * @returns {{usersList: Array<{id: string, name: string, email: string, countryCode: string, timezone: string, roles: Array<string>, lang: string, companyId: string, partner: any, partnerName: string, status: string, lastLogin: string, createdAt: string, updatedAt: string}>, loading: boolean}} - The formatted users list and loading state.
 */
export function useUsersList(searchString = "", statusFilter = "") {
  const { data, loading, error } = useQuery<any>(GET_USERS_LIST, {
    variables: {
      searchString,
      statusFilter,
    },
  });

  useEffect(() => {
    if (error) {
      enqueueSnackbar(error.message, { variant: "error" });
    }
  }, [error]);

  // Extracting the necessary data and formatting it as needed
  const formattedData =
    data?.allUsers.map(
      (user: {
        id: string;
        name: string;
        email: string;
        countryCode: string;
        timezone: string;
        roles: string[];
        lang: string;
        companyId: string;
        partner?: { name?: string } | null;
        status: string;
        isVerified?: boolean;
        lastLogin: string;
        createdAt: string;
        updatedAt: string;
      }) => ({
        id: user.id,
        name: user.name,
        email: user.email,
        countryCode: user.countryCode,
        timezone: user.timezone,
        roles: user.roles,
        lang: user.lang,
        companyId: user.companyId,
        partner: user.partner,
        status: user.status,
        isVerified: Boolean(user.isVerified),
        lastLogin: user.lastLogin,
        createdAt: user.createdAt,
        updatedAt: user.updatedAt,
        partnerName: user.partner ? user.partner.name : "N/A",
      }),
    ) || [];

  return {
    usersList: formattedData,
    loading,
  };
}
